import { MarginOrderDirection, MarginOrderType } from '@swyftx/margin/build/types';

import { create } from 'zustand';

import { MarginTakeProfitStopLossType } from './useMarginTradePanelStore.types';

type MarginState = {
  tradePrice: { [key in MarginOrderDirection]: string };
  setTradePrice: (side: MarginOrderDirection, price: string) => void;

  tradeLots: { [key in MarginOrderDirection]: string };
  setTradeLots: (side: MarginOrderDirection, lots: string) => void;

  takeProfit: { [key in MarginOrderDirection]: MarginTakeProfitStopLossType };
  setTakeProfit: (side: MarginOrderDirection, values: Partial<MarginTakeProfitStopLossType>) => void;

  stopLoss: { [key in MarginOrderDirection]: MarginTakeProfitStopLossType };
  setStopLoss: (side: MarginOrderDirection, values: Partial<MarginTakeProfitStopLossType>) => void;

  tradeSide: MarginOrderDirection;
  setTradeSide: (tradeSide: MarginOrderDirection) => void;

  tradeType: MarginOrderType;
  setTradeType: (tradeType: MarginOrderType) => void;

  reset: () => void;
};

const useMarginTradePanelStore = create<MarginState>()((set) => ({
  tradePrice: { [MarginOrderDirection.Long]: '', [MarginOrderDirection.Short]: '' },
  setTradePrice: (side: MarginOrderDirection, price: string) => {
    set(({ tradePrice }) => ({ tradePrice: { ...tradePrice, [side]: price } }));
  },

  tradeLots: { [MarginOrderDirection.Long]: '', [MarginOrderDirection.Short]: '' },
  setTradeLots: (side: MarginOrderDirection, lots: string) => {
    set(({ tradeLots }) => ({ tradeLots: { ...tradeLots, [side]: lots } }));
  },

  takeProfit: {
    [MarginOrderDirection.Long]: { checked: false, price: '' },
    [MarginOrderDirection.Short]: { checked: false, price: '' },
  },
  setTakeProfit: (side: MarginOrderDirection, values: Partial<MarginTakeProfitStopLossType>) => {
    set(({ takeProfit }) => ({ takeProfit: { ...takeProfit, [side]: { ...takeProfit[side], ...values } } }));
  },

  stopLoss: {
    [MarginOrderDirection.Long]: { checked: false, price: '' },
    [MarginOrderDirection.Short]: { checked: false, price: '' },
  },
  setStopLoss: (side: MarginOrderDirection, values: Partial<MarginTakeProfitStopLossType>) => {
    set(({ stopLoss }) => ({ stopLoss: { ...stopLoss, [side]: { ...stopLoss[side], ...values } } }));
  },

  tradeSide: MarginOrderDirection.Long,
  setTradeSide: (tradeSide: MarginOrderDirection) => {
    set({
      tradeSide,
      tradePrice: { [MarginOrderDirection.Long]: '', [MarginOrderDirection.Short]: '' },
      tradeLots: { [MarginOrderDirection.Long]: '', [MarginOrderDirection.Short]: '' },
      takeProfit: {
        [MarginOrderDirection.Long]: { checked: false, price: '' },
        [MarginOrderDirection.Short]: { checked: false, price: '' },
      },
      stopLoss: {
        [MarginOrderDirection.Long]: { checked: false, price: '' },
        [MarginOrderDirection.Short]: { checked: false, price: '' },
      },
    });
  },

  tradeType: MarginOrderType.Market,
  setTradeType: (tradeType: MarginOrderType) => {
    set({
      tradeType,
      tradePrice: { [MarginOrderDirection.Long]: '', [MarginOrderDirection.Short]: '' },
      tradeLots: { [MarginOrderDirection.Long]: '', [MarginOrderDirection.Short]: '' },
      takeProfit: {
        [MarginOrderDirection.Long]: { checked: false, price: '' },
        [MarginOrderDirection.Short]: { checked: false, price: '' },
      },
      stopLoss: {
        [MarginOrderDirection.Long]: { checked: false, price: '' },
        [MarginOrderDirection.Short]: { checked: false, price: '' },
      },
    });
  },

  reset: () => {
    set({
      tradePrice: {
        [MarginOrderDirection.Long]: '',
        [MarginOrderDirection.Short]: '',
      },
      tradeLots: {
        [MarginOrderDirection.Long]: '',
        [MarginOrderDirection.Short]: '',
      },
      stopLoss: {
        [MarginOrderDirection.Long]: { checked: false, price: '' },
        [MarginOrderDirection.Short]: { checked: false, price: '' },
      },
      takeProfit: {
        [MarginOrderDirection.Long]: { checked: false, price: '' },
        [MarginOrderDirection.Short]: { checked: false, price: '' },
      },
    });
  },
}));

export { useMarginTradePanelStore };
